import React, { useState } from 'react';

import { Alert, Upload } from 'antd-v5';
import type { UploadProps } from 'antd-v5';
import { MdInsertDriveFile } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';

import styles from './ImportDropzone.module.scss';

const toKb = (bytes: number) => Math.ceil(bytes / 1024);

interface ImportDropzoneProps {
  /** Accepted file extensions, e.g. '.txt' */
  accept: string;
  /** Primary instruction inside the dropzone */
  dropLabel: React.ReactNode;
  /** In bytes; larger files are rejected instead of reaching `onSelectFile` */
  maxSize?: number;
  disabled?: boolean;
  onSelectFile: (file: File) => void;
}

const ImportDropzone: React.FC<ImportDropzoneProps> = props => {
  const { accept, dropLabel, maxSize, disabled, onSelectFile } = props;

  const [rejected, setRejected] = useState<{
    name: string;
    size: number;
  } | null>(null);

  const beforeUpload: NonNullable<UploadProps['beforeUpload']> = file => {
    if (maxSize && file.size > maxSize) {
      setRejected({ name: file.name, size: file.size });
    } else {
      setRejected(null);
      onSelectFile(file as File);
    }
    // Prevent antd from auto-uploading - the consumer handles the upload
    return Upload.LIST_IGNORE;
  };

  return (
    <React.Fragment>
      <Upload.Dragger
        className={styles.dragger}
        accept={accept}
        multiple={false}
        maxCount={1}
        showUploadList={false}
        disabled={disabled}
        beforeUpload={beforeUpload}
      >
        <div className={styles.content}>
          <MdInsertDriveFile className={styles.icon} />
          <div className={styles.instructions}>
            <Text font="body-body-3">
              <FormattedMessage id="advancementImport.importLimit" />
            </Text>
            <Text font="body-body-3">{dropLabel}</Text>
            {!!maxSize && (
              <Text font="body-caption">
                <FormattedMessage
                  id="advancementImport.maxFileSize"
                  values={{ max: toKb(maxSize) }}
                />
              </Text>
            )}
            <Text font="body-body-3">
              <FormattedMessage id="advancementImport.or" />
            </Text>
          </div>
          <Button type="default" disabled={disabled}>
            <FormattedMessage id="advancementImport.browse" />
          </Button>
        </div>
      </Upload.Dragger>
      {!!maxSize && !!rejected && (
        <Alert
          className={styles.error}
          type="error"
          showIcon
          message={
            <FormattedMessage
              id="advancementImport.fileTooLarge"
              values={{
                fileName: <strong>{rejected.name}</strong>,
                size: toKb(rejected.size),
                limit: (
                  <strong>
                    <FormattedMessage
                      id="advancementImport.fileTooLarge.limit"
                      values={{ max: toKb(maxSize) }}
                    />
                  </strong>
                ),
              }}
            />
          }
        />
      )}
    </React.Fragment>
  );
};

export default ImportDropzone;
